import React, { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { NavLink } from 'react-router-dom';

import axios, { AxiosResponse } from 'axios';

import { Breadcrumb } from '@shared/components/bootstrap';

import { PageHeader } from '@admin/components/helpers/page_header';
import { Spinner } from '@admin/components/spinner';
import { useAxiosRequest } from '@admin/hooks/use_axios_request';
import { useNavigate } from '@admin/hooks/use_navigate';
import { useTitle } from '@admin/hooks/use_title';

import { TransferForm } from './form';

type Barcode = {
  value: string;
};

type Image = {
  id: number;
  state: string;
  source: string;
};

type Item = {
  id: number;
  hero: number;
  images: Image[];
};

type Pallet = {
  id: number;
  state: string;
  barcode: Barcode;
  items: Item[];
};

type Warehouse = {
  id: number;
  name: string;
};

type Transfer = {
  id: number;
  state: string;
  origin: Warehouse;
  destination: Warehouse;
  pallets: Pallet[];
};

const useTransferRequest = (id: string | number) => {
  const [request, setRequest] = useState<Promise<AxiosResponse<Transfer>> | undefined>(undefined);
  useEffect(() => {
    setRequest(axios.get<Transfer>(`/transfers/${id}.json`));
  }, [id]);

  return useAxiosRequest(request);
};

const useTransferSaveRequest = () => {
  const [request, setRequest] = useState<Promise<AxiosResponse<Transfer>> | undefined>(undefined);
  const execute = ({
    id,
    originID,
    destinationID,
    palletIDs,
  }: {
    id: number | string;
    originID: number;
    destinationID: number;
    palletIDs: number[];
  }) => {
    const request = axios.patch<Transfer>(`/transfers/${id}.json`, {
      origin_id: originID,
      destination_id: destinationID,
      pallet_ids: palletIDs,
    });
    setRequest(request);
    return request;
  };

  return [execute, useAxiosRequest(request)] as const;
};

export const TransferEdit: FC = () => {
  useTitle('Transfer');
  const { id } = useParams<{ id: string }>();
  const [error, setError] = useState<string | undefined>(undefined);
  const { response } = useTransferRequest(id);
  const transfer = response?.data;

  const navigate = useNavigate();
  const [save, { loading }] = useTransferSaveRequest();

  if (!transfer) return <Spinner />;

  const onSave = async (form: { originID: number; destinationID: number; palletIDs: number[] }) => {
    try {
      setError(undefined);
      await save({
        id,
        originID: form.originID,
        destinationID: form.destinationID,
        palletIDs: form.palletIDs,
      });
      navigate(`/transfers/${id}`);
    } catch (error) {
      if (axios.isAxiosError(error)) setError(error.response?.data?.message ?? 'An unknown error occurred.');
      else setError(error instanceof Error ? error.message : 'An unknown error occurred.');
    }
  };

  return (
    <>
      <PageHeader>
        <Breadcrumb>
          <Breadcrumb.Item>
            <NavLink to="/transfers">Transfers</NavLink>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <NavLink to={`/transfers/${id}`}>Transfer #{id}</NavLink>
          </Breadcrumb.Item>
          <Breadcrumb.Item active>Edit</Breadcrumb.Item>
        </Breadcrumb>
      </PageHeader>
      <TransferForm error={error} loading={loading} transfer={transfer} onSave={onSave} />
    </>
  );
};
